import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridBaseQuery } from '@core';
import { InvestigationFormEntity } from '../../domain/entities/investigation-form.entity';
import { InvestigationFormApi } from '../api/investigation-form.api';
import { AddFieldRequestDto } from '../dtos/add-field-request.dto';
import { FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';
import { FieldDefinitionLibraryResponseDto } from '../dtos/field-definition-library-response.dto';
import { FormVersionDetailRepositoryResponseDto } from '../dtos/form-version-detail-repository-response.dto';
import { FormVersionsListResponseDto } from '../dtos/form-versions-list-response.dto';
import { ReorderFieldsRequestDto } from '../dtos/reorder-fields-request.dto';
import { UpdateFieldRequestDto } from '../dtos/update-field-request.dto';
import { CreateInvestigationFormRequestDto } from '../dtos/create-investigation-form-request.dto';
import { InvestigationFormMapper, UpdateInvestigationFormParams } from '../mappers/investigation-form.mapper';

@Injectable({ providedIn: 'root' })
export class InvestigationFormRepository {
  private readonly _api = inject(InvestigationFormApi);
  private readonly _mapper = inject(InvestigationFormMapper);

  public getAllInvestigationForms(): Observable<InvestigationFormEntity[]> {
    return this._api.getAllInvestigationForms().pipe(map((dtos) => this._mapper.toDomainArray(dtos)));
  }

  public getInvestigationFormsWithPaging(query: GridBaseQuery): Observable<{ data: InvestigationFormEntity[]; totalElements: number }> {
    return this._api.getInvestigationFormsWithPaging(query).pipe(
      map((result) => ({
        data: (result.data || []).map((dto) => this._mapper.toDomain(dto)),
        totalElements: result.totalElements || 0,
      })),
    );
  }

  public getInvestigationFormsByInvestigationTypeId(investigationTypeId: string): Observable<InvestigationFormEntity[]> {
    return this._api.getInvestigationFormsByInvestigationTypeId(investigationTypeId).pipe(
      map((dtos) => this._mapper.toDomainArray(dtos))
    );
  }

  public getInvestigationFormByInvestigationTypeId(investigationTypeId: string): Observable<InvestigationFormEntity | undefined> {
    return this._api.getInvestigationFormByInvestigationTypeId(investigationTypeId).pipe(
      map((dto) => (dto ? this._mapper.toDomain(dto) : undefined))
    );
  }

  public getInvestigationFormById(id: string): Observable<InvestigationFormEntity | undefined> {
    return this._api.getInvestigationFormById(id).pipe(
      map((dto) => (dto ? this._mapper.toDomain(dto) : undefined))
    );
  }

  public getInvestigationFormVersionsByFormId(formId: string): Observable<FormVersionsListResponseDto> {
    return this._api.getInvestigationFormVersionsByFormId(formId);
  }

  public getInvestigationFormVersionDetailByVersionId(versionId: string): Observable<FormVersionDetailRepositoryResponseDto> {
    return this._api.getInvestigationFormVersionDetailByVersionId(versionId).pipe(
      map((dto) => this._mapper.mapFormVersionDetail(dto))
    );
  }

  public createInvestigationFormDraftVersion(formId: string): Observable<{ versionId: string }> {
    return this._api.createInvestigationFormDraftVersion(formId);
  }

  public publishDraft(formId: string): Observable<{ versionId: string }> {
    return this._api.publishDraft(formId);
  }

  public discardDraft(formId: string): Observable<void> {
    return this._api.discardDraft(formId);
  }

  public updateInvestigationForm(id: string, params: UpdateInvestigationFormParams): Observable<void> {
    const dto = this._mapper.toUpdateDto(params);
    return this._api.updateInvestigationForm(id, dto);
  }

  public deleteInvestigationForm(id: string): Observable<void> {
    return this._api.deleteInvestigationForm(id);
  }

  public addField(versionId: string, dto: AddFieldRequestDto): Observable<{ id: string }> {
    return this._api.addField(versionId, dto);
  }

  public updateField(versionId: string, fieldId: string, dto: UpdateFieldRequestDto): Observable<{ id: string }> {
    return this._api.updateField(versionId, fieldId, dto);
  }

  public removeField(versionId: string, fieldId: string): Observable<void> {
    return this._api.removeField(versionId, fieldId);
  }

  public reorderFields(versionId: string, dto: ReorderFieldsRequestDto): Observable<void> {
    return this._api.reorderFields(versionId, dto);
  }

  public getFieldDefinitions(): Observable<FieldDefinitionLibraryResponseDto> {
    return this._api.getFieldDefinitions();
  }

  public getFieldTypes(): Observable<FieldTypeResponseDto[]> {
    return this._api.getFieldTypes();
  }

  public createInvestigationForm(params: {
    name: string;
    description?: string;
    investigationTypeId?: string;
  }): Observable<{ formId: string; versionId: string }> {
    const dto: CreateInvestigationFormRequestDto = { ...params };
    return this._api.createInvestigationForm(dto);
  }
}
